import { AdvancedFilter, AdvancedFilterProps, AdvancedFilterSelectedOptions, SubfilterType } from '@sendible/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { advancedLocalStorage } from '@sendible/common';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { useGetFilters } from '@sendible/frontendv2/src/models/filters';
import { Container } from './index.styles';
import { useCalendarContext } from '../../context';
import { getStatusIcon, networkIconNameMap, removePausedQueuesFromSelectedFilters } from '../../utils';

type FilterItemWithExtras = FilterItem & {
  networks?: MessageChannels[];
  showAsAvatar?: boolean;
  paused?: boolean;
};

export type SubfilterWithPaused = SubfilterType & {
  rightLabel?: string;
};

const API_PARAM_KEY_MAP: Record<string, string> = {
  users: 'userIds',
  profiles: 'profileIds',
  profileLists: 'profileListIds',
  queues: 'queueIds',
  campaigns: 'campaignIds',
};

const STORAGE_KEY = 'newcalendar_filters';

const STATUS_PARAM_TO_FILTER_ID: Record<string, string> = {
  scheduled: 'Pending',
  sent: 'Sent',
  failed: 'Bounced',
};

const getPausedQueueIdsFromFiltersData = (dt?: FilterData): string[] => {
  const queues = (dt?.queues ?? []) as FilterItemWithExtras[];

  const pausedIds = queues.filter((q) => q.paused === true || q.metadata?.isPaused === true).map((q) => String(q.id));

  return [...new Set(pausedIds)];
};

export const FilterBar = () => {
  const user = useBridgeUser();
  const { setFilterParams } = useCalendarContext();
  const [selectedFiltersByScope, setSelectedFiltersByScope] = useState<Record<string, AdvancedFilterSelectedOptions[]>>({});
  const { data, isLoading } = useGetFilters([]);
  const pausedQueuesIds = useMemo(() => getPausedQueueIdsFromFiltersData(data as FilterData | undefined), [data]);
  const { t } = useTranslation(['calendar', 'common_terms']);
  const [searchParams] = useSearchParams();
  const activeStatus = searchParams.get('view');
  const activeView = (['main', 'scheduled', 'sent', 'failed'] as const).includes(activeStatus as never) ? (activeStatus as string) : 'main';
  const isMain = activeView === 'main';
  const currentUserId = typeof user?.userId === 'undefined' ? undefined : String(user.userId);
  const storageKey = `${STORAGE_KEY}_${activeView}`;
  const scopeKey = `${activeView}:${currentUserId ?? 'anonymous'}`;

  const isUrl = (s?: string) => !!s && /^(https?:)?\/\//i.test(s);

  const normalizeImageUrl = (url?: string): string | undefined => {
    const u = url?.trim();

    if (!u) return undefined;

    if (u.startsWith('//')) return `https:${u}`; // Protocol-relative -> https
    if (u.startsWith('http://')) return `https://${u.slice('http://'.length)}`; // Avoid mixed-content blocks in https pages
    if (u.startsWith('/') && typeof window !== 'undefined') return `${window.location.origin}${u}`; // Relative path -> same origin

    return u;
  };

  const toSubfilter = (item: FilterItemWithExtras, groupKey: keyof FilterData, rightLabelOverride?: string): SubfilterWithPaused => {
    const showAsAvatar = groupKey === 'profiles' ? true : Boolean(item.showAsAvatar);
    const imageUrl =
      groupKey === 'queues' || groupKey === 'users'
        ? undefined
        : groupKey === 'profiles'
          ? normalizeImageUrl(item.icon ?? undefined)
          : isUrl(item.icon)
            ? item.icon
            : undefined;
    const iconKey = groupKey === 'profiles' ? (item.networks?.[0] ?? '') : item.id;
    const statusIcon = groupKey === 'statuses' ? getStatusIcon(item.id as CalendarStatuses, false) : undefined;
    const iconName = statusIcon?.icon || networkIconNameMap[iconKey];
    const workspace = item.metadata?.workspace as { color?: string; name?: string } | undefined;
    const avatarColor = groupKey === 'queues' && workspace?.color ? workspace.color : undefined;
    const avatarLabel = groupKey === 'queues' && workspace?.name ? workspace.name : undefined;

    return {
      id: item.id,
      label: item.label || t(item.id, { ns: 'calendar' }),
      subLabel: item.subLabel,
      icon: iconName,
      iconColor: statusIcon?.color,
      imageUrl,
      showAsAvatar,
      avatarColor,
      avatarLabel,
      rightLabel: rightLabelOverride ?? (groupKey === 'queues' && item.paused ? (t('paused', { ns: 'calendar' }) as string) : undefined),
      isDisabled: groupKey === 'queues' && item.paused ? true : undefined,
    };
  };

  const buildFilters = (dt?: FilterData): AdvancedFilterProps['filters'] => {
    if (!dt) return [];

    const groupKeys: (keyof FilterData)[] = [
      'statuses',
      'networks',
      'mediaTypes',
      'users',
      'profiles',
      'profileLists',
      'postTypes',
      'queues',
      'campaigns',
    ];

    const result: AdvancedFilterProps['filters'] = [];

    const currentOriginalUserId = String(user?.originalUserId);

    for (const key of groupKeys) {
      const items = (dt[key] ?? []) as FilterItemWithExtras[];

      if (!items.length) continue;

      if (key === 'users') {
        const sorted = [...items].sort((a, b) => {
          if (String(a.id) === currentOriginalUserId) return -1;
          if (String(b.id) === currentOriginalUserId) return 1;

          return 0;
        });

        result.push({
          id: key,
          label: t('author', { ns: 'calendar' }),
          subFilters: sorted.map((it) => {
            const isWorkspace = it.metadata?.isWorkspace === true;
            const rightLabel = isWorkspace
              ? (t('automated_posting', { ns: 'calendar' }) as string)
              : String(it.id) === currentOriginalUserId
                ? (t('you', { ns: 'calendar' }) as string)
                : undefined;

            return toSubfilter(it, key, rightLabel);
          }),
        });

        continue;
      }

      result.push({
        id: key,
        label: t(key, { ns: 'calendar' }),
        subFilters: items.map((it) => toSubfilter(it, key)),
      });
    }

    return result;
  };

  const filters = useMemo(() => {
    const built = buildFilters(data as FilterData);

    if (isMain) return built;

    return built.map((f) => {
      if ('subFilters' in f && f.id === 'statuses') {
        return { ...f, subFilters: f.subFilters.map((sf) => ({ ...sf, isDisabled: true })) };
      }

      return f;
    });
  }, [data, user?.userId, isMain]);

  const forcedStatusFilter = useMemo((): AdvancedFilterSelectedOptions | null => {
    if (isMain) return null;

    const filterId = activeStatus ? STATUS_PARAM_TO_FILTER_ID[activeStatus] : null;

    if (!filterId) return null;

    return {
      id: 'statuses',
      label: t('statuses', { ns: 'calendar' }),
      selected: [filterId],
    };
  }, [isMain, activeStatus, t]);

  const selectedFilters = useMemo((): AdvancedFilterSelectedOptions[] => {
    const inMemory = selectedFiltersByScope[scopeKey];

    if (inMemory) return inMemory;

    const raw = advancedLocalStorage.getItem(storageKey);

    if (!raw || typeof user?.userId === 'undefined') return [];

    try {
      const all = raw as Record<string, AdvancedFilterSelectedOptions[]>;
      const saved = all?.[String(user.userId)];

      if (!Array.isArray(saved)) return [];

      const baseFilters = isMain ? saved : saved.filter((f: AdvancedFilterSelectedOptions) => f.id !== 'statuses');
      const { cleaned } = removePausedQueuesFromSelectedFilters(baseFilters, pausedQueuesIds);

      return cleaned;
    } catch {
      return [];
    }
  }, [selectedFiltersByScope, scopeKey, storageKey, user, isMain, pausedQueuesIds]);

  const effectiveSelectedFilters = useMemo((): AdvancedFilterSelectedOptions[] => {
    if (isMain) return selectedFilters;

    const withoutStatus = selectedFilters.filter((f) => f.id !== 'statuses');

    if (!forcedStatusFilter) return withoutStatus;

    return [forcedStatusFilter, ...withoutStatus];
  }, [isMain, selectedFilters, forcedStatusFilter]);

  const apiFilterParams = useMemo(() => {
    return effectiveSelectedFilters.reduce<Record<string, string>>((acc, group) => {
      if (!group.selected.length) return acc;

      const apiKey = API_PARAM_KEY_MAP[group.id] || group.id;

      acc[apiKey] = group.selected.join(',');

      return acc;
    }, {});
  }, [effectiveSelectedFilters]);

  const handleSetSelectedFilters = (options: AdvancedFilterSelectedOptions[]) => {
    // Strip forced status from options — it's controlled by URL param, not user selection.
    const baseOptions = isMain ? options : options.filter((f) => f.id !== 'statuses');
    const { cleaned } = removePausedQueuesFromSelectedFilters(baseOptions, pausedQueuesIds);

    setSelectedFiltersByScope((previous) => ({
      ...previous,
      [scopeKey]: cleaned,
    }));

    if (typeof user?.userId === 'undefined') return;

    const raw = advancedLocalStorage.getItem(storageKey);
    let all: Record<string, AdvancedFilterSelectedOptions[]> = {};

    if (raw && typeof raw === 'object') {
      all = raw as Record<string, AdvancedFilterSelectedOptions[]>;
    }

    all[String(user.userId)] = cleaned;
    advancedLocalStorage.setItem(storageKey, all);
  };

  useEffect(() => {
    setFilterParams(apiFilterParams);
  }, [apiFilterParams, setFilterParams]);

  return (
    <Container
      data-testid="calendar-filter-bar"
      data-export-hide
    >
      <AdvancedFilter
        clearAllLabel={t('clearAll', { ns: 'common_terms' }) as string}
        displayLabel={t('filter', { ns: 'calendar' }) as string}
        emptyFilterText={t('no_filters', { ns: 'calendar' }) as string}
        filters={filters}
        filterResultsText={t('contains', { ns: 'calendar' }) as string}
        icon="menu_kebab"
        inputLabel={t('filter_search', { ns: 'calendar' }) as string}
        isDisabled={isLoading}
        lockedFilterIds={isMain ? [] : ['statuses']}
        onSelect={handleSetSelectedFilters}
        selectedFilters={effectiveSelectedFilters}
        selectedAllLabel={t('selectAll', { ns: 'common_terms' }) as string}
        unselectedAllLabel={t('unselectAll', { ns: 'common_terms' }) as string}
      />
    </Container>
  );
};
